<script setup>
import OffersTable from '@/components/table/Table.vue';
import PreviewModal from '@/components/modals/PreviewModal.vue';
import { useToast } from '@/composables/use-toast.js';
import { useStorage } from '@/composables/use-storage.js';
import { ref, onMounted, watch, nextTick, computed } from 'vue';
import axios from 'axios';
import BaseCardTable from '@/components/table/BaseCardTable.vue';

const { userInfo } = useStorage();
const { addToast } = useToast();

const quickFilterText = ref('');
const showModal = ref(false);
const currentRowNode = ref(null);
const rowData = ref([]);
const cardData = ref([]);
const reason = ref('');
const user = ref(userInfo.value);
const loading = ref(false);

const currentPage = ref(1);
const itemsPerPage = ref(10);
const sortBy = ref('created_at');
const sortOrder = ref('desc');
watch(showModal, (newValue) => {
    if (!newValue) {
        currentRowNode.value = null;
    }
});

const truncateText = (text, maxLength = 44) => {
    return text?.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const filteredData = computed(() => {
    if (!quickFilterText.value) return rowData.value;
    
    const searchTerm = quickFilterText.value.toLowerCase();
    return rowData.value.filter(item => 
        Object.values(item).some(value => 
            String(value).toLowerCase().includes(searchTerm)
        )
    );
});

const sortedData = computed(() => {
    if (!sortBy.value) return filteredData.value;
    
    return [...filteredData.value].sort((a, b) => {
        let aValue = a[sortBy.value];
        let bValue = b[sortBy.value];
        
        // Handle date sorting
        if (sortBy.value.includes('date') || sortBy.value === 'created_at') {
            aValue = new Date(aValue).getTime();
            bValue = new Date(bValue).getTime();
        }
        
        // Handle amount sorting
        if (sortBy.value === 'amount') {
            aValue = parseFloat(String(aValue).replace(/[^0-9.-]/g, ''));
            bValue = parseFloat(String(bValue).replace(/[^0-9.-]/g, ''));
        }
        
        if (aValue < bValue) return sortOrder.value === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder.value === 'asc' ? 1 : -1;
        return 0;
    });
});

const totalItems = computed(() => filteredData.value.length);

onMounted(async () => {
    loading.value = true;
    try {
        const response = await axios.get('/api/bids/incoming-bids');
        rowData.value = response.data.map(offer => {
            return {
                bid_id: offer?.id,
                dataset_name: offer?.listing?.datasets?.length > 0 ? offer?.listing?.datasets[0]?.name : 'No dataset',
                dataset_tooltip: offer?.listing?.datasets?.length > 0 ? offer?.listing?.datasets[0]?.name : 'No dataset',
                listing_name: offer?.listing?.name,
                listing_id: offer?.listing?.id,
                created_at: offer?.created_at,
                expiration_date: offer?.expires_at,
                amount: offer?.bid_amount,
                currency: offer?.currency,
                status: offer?.status,
                bidder: offer?.bidder?.username,
                bidder_tooltip: offer?.bidder?.username,
                bidder_id: offer?.bidder?.id,
                seller: offer?.seller?.username,
                seller_id: offer?.seller?.id
            }
        });
        cardData.value = response.data.map(offer => ({
            id: offer.id,
            title: offer?.listing?.name,
            actionEnabled: offer.status === 'PENDING',
            rows: [
                { column: 'Bidder', type: 'text', value: offer.bidder.username },
                { column: 'Dataset', type: 'text', value: offer.listing.datasets?.length > 0 ? offer.listing.datasets[0].name : 'No dataset' },
                { column: 'Created', type: 'date', value: offer.created_at },
                { column: 'Expires', type: 'date', value: offer.expires_at },
                { column: 'Amount', type: 'text', value: offer.bid_amount },
                { column: 'Status', type: 'tag', value: getBadgeConfig(offer.status).text, color: getBadgeConfig(offer.status).type },
        ]
        }));
    } catch (error) {
        console.error('Error fetching offers:', error);
        addToast('Error fetching offers', 'error');
    } finally {
        loading.value = false;
    }
});

const getBadgeConfig = (status) => {
    switch (status) {
        case 'PENDING':
            return { text: 'Needs Review', type: 'info' };
        case 'ACCEPTED':
            return { text: 'Accepted', type: 'success' };
        case 'REJECTED':
            return { text: 'Rejected', type: 'error' };
        case 'EXPIRED':
            return { text: 'Expired', type: 'warning' };
        case 'PAID':
            return { text: 'Paid', type: 'success' };
        default:
            return { text: status, type: 'default' };
    }
};

const handleReviewClick = (item) => {
    if (item.status !== 'PENDING') return;
    currentRowNode.value = item;
    showModal.value = true;
};

const handleSort = (columnKey) => {
    if (sortBy.value === columnKey) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortBy.value = columnKey;
        sortOrder.value = 'asc';
    }
};

const handleCardAction = (id) => {
    const rowNode = cardData.value.find(item => item.id === id);
    currentRowNode.value = rowNode;
    showModal.value = true;
}

const handleReviewOffer = async (isAccepted, bid_id) => {
    if (isAccepted === 'close' || !bid_id) return;
    try {
        let response;
        if (isAccepted === 'accept') {
            response = await axios.post(`/api/bids/${bid_id}/accept`, {
                id: bid_id,
                note: 'Accepted by ' + user.value.username
            });
        } else {
            response = await axios.post(`/api/bids/${bid_id}/reject`, {
                id: bid_id,
                reason: 'Bidder ' + user.value.username + ' did not accept the offer, Reason: ' + reason.value
            });
        }

        if (response.data.status === 200 || response.data.status === 201) {
            const updatedData = updateRowData(response.data);
            if (updatedData) {
                rowData.value = [...updatedData];
                await nextTick();
            }
            addToast(response.data.message);
        }
    } catch (error) {
        console.error('Error updating offer:', error);
        addToast(error.response.data.message, 'error');
    } finally {
        const refreshResponse = await axios.get('/api/bids/incoming-bids');
        rowData.value = refreshResponse.data.map(offer => ({
            bid_id: offer?.id,
            dataset_name: truncateText(offer?.listing?.datasets?.length > 0 ? offer?.listing?.datasets[0]?.name : 'No dataset'),
            dataset_tooltip: offer?.listing?.datasets?.length > 0 ? offer?.listing?.datasets[0]?.name : 'No dataset',
            listing_name: truncateText(offer?.listing?.name),
            listing_id: offer?.listing?.id,
            receipt_address: offer?.listing?.receipt_address,
            created_at: offer?.created_at,
            expiration_date: offer?.expires_at,
            amount: offer?.bid_amount,
            currency: offer?.currency,
            status: offer?.status,
            bidder: truncateText(offer?.bidder?.username, 8),
            bidder_tooltip: offer?.bidder?.username,
            bidder_id: offer?.bidder?.id,
            seller: offer?.seller?.username,
            seller_tooltip: offer?.listing?.datasets?.length > 0 ? offer?.listing?.datasets[0]?.seller?.user?.username : 'No seller',
            seller_id: offer?.seller?.id
        }));

        cardData.value = refreshResponse.data.map(offer => ({
            id: offer.id,
            title: offer?.listing?.name,
            actionEnabled: offer.status === 'PENDING',
            rows: [
                { column: 'Bidder', type: 'text', value: offer.bidder.username },
                { column: 'Dataset', type: 'text', value: offer.listing.datasets?.length > 0 ? offer.listing.datasets[0].name : 'No dataset' },
                { column: 'Created', type: 'date', value: offer.created_at },
                { column: 'Expires', type: 'date', value: offer.expires_at },
                { column: 'Amount', type: 'text', value: offer.bid_amount },
                { column: 'Status', type: 'tag', value: getBadgeConfig(offer.status).text, color: getBadgeConfig(offer.status).type },
        ]
        }));
        await nextTick();
        showModal.value = false;
    }
}

const updateRowData = (data) => {
    if (!data?.bids) return;
    return data?.bids?.map(offer => {
        return {
            bid_id: offer?.id,
            dataset_name: truncateText(offer?.listing?.datasets[0]?.name),
            dataset_tooltip: offer?.listing?.datasets[0]?.name,
            listing_name: truncateText(offer?.listing?.name),
            listing_id: offer?.listing?.id,
            receipt_address: offer?.listing?.receipt_address,
            created_at: offer?.created_at,
            expiration_date: offer?.expires_at,
            amount: offer?.bid_amount + ' ' + offer?.currency,
            currency: offer?.currency,
            status: offer?.status,
            bidder: truncateText(offer?.bidder?.username, 8),
            bidder_tooltip: offer?.bidder?.username,
            bidder_id: offer?.bidder?.id,
            seller: offer?.seller?.username,
            seller_tooltip: offer?.listing?.datasets[0]?.seller?.user?.username,
            seller_id: offer?.seller?.id
        }
    });
}

const columns = [
    { 
        key: 'dataset_name',
        label: 'DATASET',
        sortable: true,
        type: 'text',
        truncate: 44
    },
    { 
        key: 'bidder',
        label: 'BIDDER',
        sortable: true,
        type: 'text'
    },
    { 
        key: 'created_at',
        label: 'BID RECEIVED',
        sortable: true,
        type: 'date'
    },
    { 
        key: 'expiration_date',
        label: 'EXPIRATION',
        sortable: true,
        type: 'date'
    },
    { 
        key: 'amount',
        label: 'AMOUNT',
        sortable: true,
        type: 'amount'
    },
    { 
        key: 'status',
        label: 'STATUS',
        sortable: true,
        type: 'text'
    },
    { 
        key: 'action',
        label: 'ACTION',
        sortable: false,
        type: 'text'
    }
];

</script>

<template>
    <PreviewModal v-if="showModal" v-model:show="showModal" @isConfirmed="handleReviewOffer" :bid-id="currentRowNode?.bid_id ?? currentRowNode?.id" :need-credentails="true" />
    <div>
       <BaseCardTable :items="cardData" :quickFilterText="quickFilterText" :hasAction="true" :hasActionText="'Review'" @action="handleCardAction" :isLoading="loading" class="md:hidden"/>
        <OffersTable 
            :data="sortedData" 
            :columns="columns"
            :searchQuery="quickFilterText"
            :currentPage="currentPage"
            :itemsPerPage="itemsPerPage"
            :totalItems="totalItems"
            :sortBy="sortBy"
            :sortOrder="sortOrder"
            title="My Offers"
            searchPlaceholder="Search offers..."
            emptyMessage="No offers found"
            class="hidden md:block"
            @update:searchQuery="quickFilterText = $event"
            @update:currentPage="currentPage = $event"
            @update:itemsPerPage="itemsPerPage = $event"
            @sort="handleSort"
        >
            <template #column-dataset_name="{ item }">
                <router-link :to="`/item-detail/${item.listing_id}`" class="cursor-pointer font-semibold hover:text-echoes-primary transition-colors duration-200 w-fit truncate max-w-[200px]">
                    {{ truncateText(item.dataset_name, 44) }}
                </router-link>
            </template>
            
    
            <template #column-status="{ item }">
                <span :class="[
                    'px-3 py-1 rounded-full text-xs font-medium',
                    getBadgeConfig(item.status).type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500' :
                    getBadgeConfig(item.status).type === 'error' ? 'bg-red-500/20 text-red-400 border border-red-500' :
                    getBadgeConfig(item.status).type === 'warning' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500' :
                    getBadgeConfig(item.status).type === 'info' ? 'bg-blue-500/20 text-blue-400 border border-blue-500' :
                    'bg-gray-500/20 text-gray-400 border border-gray-500'
                ]">
                    {{ getBadgeConfig(item.status).text }}
                </span>
            </template>
            
            <template #column-action="{ item }">
                <button 
                    @click="handleReviewClick(item)"
                    :disabled="item.status !== 'PENDING'"
                    :class="[
                        'flex items-center gap-2 px-3 py-1 text-sm rounded-md transition-all duration-200',
                        item.status === 'PENDING' 
                            ? 'bg-echoes-primary hover:bg-echoes-primary/80 text-white cursor-pointer' 
                            : 'bg-gray-500 opacity-40 text-gray-300 cursor-not-allowed'
                    ]"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Review
                </button>
            </template>
        </OffersTable>
    </div>
</template>